import { useConfig } from '@/hooks/useConfig/useConfig';
import { useTransaction } from '@/hooks/useTransaction/useTransaction';
import { SupportedLocales } from '@/providers/ConfigProvider/ConfigProvider';
import { useAirKeyTracePageView } from '@/utils/airKeyTrace';
import { cn } from '@/utils/cn';
import {
  Button,
  Column,
  Container,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  Row,
  TextBody,
  TextHeading,
} from '@cof/omni-react';

export const CardsPopup = ({
  onClose,
  showCardImage,
}: {
  onClose: () => void;
  showCardImage: boolean;
}) => {
  useAirKeyTracePageView('card_details_modal');
  const { locale, content } = useConfig();
  const { publicData } = useTransaction();
  const cards = publicData?.cardLast4Array || [];
  const map = publicData?.cardProductMap;
  const cardProducts = (Array.isArray(map) ? map : [map || {}]).flatMap(
    Object.entries,
  ) as [string, string][];

  const translations: {
    [k in SupportedLocales]: { title: string; body: string };
  } = {
    'en-US': {
      title: "You'll need one of these cards",
      body: 'To complete verification, use one of your cards ending in:',
    },
    'es-US': {
      title: 'Necesitarás una de estas tarjetas',
      body: 'Para completar la verificación, usa una de tus tarjetas que termine en:',
    },
  };

  const t = translations[locale];

  const modalContent = () => {
    if (!showCardImage) {
      return (
        <Column className={cn('text-neutral-primary')}>
          <TextBody>{t.body}</TextBody>
          <ul>
            {cards.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </Column>
      );
    }
    return (
      <Column className={cn('text-neutral-primary')}>
        {cardProducts.map(([cardLast4, productName]) => (
          <Row key={cardLast4} paddingTop="medium">
            <Container paddingRight="medium">
              <img
                src={`${content.config.urlBase}${productName.replace(/\s/g, '').toLowerCase()}.svg`}
                onError={(e) => {
                  e.currentTarget.src = `${content.config.urlBase}generic.svg`;
                }}
                width={96}
                alt="card"
                role="presentation"
              />
            </Container>
            {typeof productName === 'string'
              ? productName
              : JSON.stringify(productName)}
            ...{cardLast4}
          </Row>
        ))}
      </Column>
    );
  };

  return (
    <Modal open={true}>
      <Dialog className={cn('bg-base1')}>
        <DialogHeader paddingBottom="zero">
          <TextHeading
            fontWeight="semibold"
            className={cn('text-neutral-primary')}
          >
            {t.title}
          </TextHeading>
        </DialogHeader>
        <DialogContent paddingBottom="tiny">{modalContent()}</DialogContent>
        <DialogFooter padding="medium">
          <Column padding="small" alignItems="flex-end">
            <Button
              size="medium"
              className={cn('text-interactive')}
              rank="tertiary"
              onClick={onClose}
            >
              Close
            </Button>
          </Column>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
};
